import React, { Component } from 'react';
import TextField from '@mui/material/TextField';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import { LocationDto } from '@trucktrax/trucktrax-ts-common';

import {
  AddressSuggestion,
  ResolvedAddress,
  createAutocompleteSessionToken,
  getAddressSuggestions,
  resolveAddressLocation,
} from '../../../../util/placesUtil';
import styles from './DeliveryAddressPicker.module.css';
import {
  DELIVERY_LOCATION_ADDRESS_PROMPT,
  DELIVERY_LOCATION_ENTER_ADDRESS,
  DELIVERY_LOCATION_LOOKUP_ERROR,
  DELIVERY_LOCATION_NO_MATCHES,
  DELIVERY_LOCATION_SEARCHING,
  DELIVERY_LOCATION_SELECT_MATCH,
} from '../../../../constants/deliveryLocationConstants';

// Feature-owned Places lookup policy (kept out of the shared placesUtil): bias ~50 mi
// around the ticket's current location, US street addresses only.
const ADDRESS_BIAS_RADIUS_METERS = 80000;
const ADDRESS_COUNTRY = 'us';
const ADDRESS_TYPES = ['address'];
const SUGGEST_DEBOUNCE_MS = 600;

// The single source of truth for what the picker is showing. Collapses what used to
// be three correlated booleans (matches/selected/searching) into one phase, so every
// render branch is reachable by construction and no stale pick-list can linger.
type AddressPhase =
  | { kind: 'idle' }
  | { kind: 'searching' }
  | { kind: 'matches'; matches: AddressSuggestion[] }
  | { kind: 'noMatches' }
  | { kind: 'confirmed' };

type MessageKind = 'searching' | 'no-matches' | 'empty';

export interface DeliveryAddressPickerProps {
  // Coordinates to rank nearby predictions first (the ticket's current location).
  bias?: LocationDto;
  // Fired when the dispatcher picks a prediction and it resolves to coordinates.
  onConfirmed: (resolved: ResolvedAddress) => void;
  // Fired whenever the field is edited/cleared, so the parent abandons a prior confirm.
  onCleared: () => void;
  // Surface a lookup failure (wired to a snackbar by the parent).
  onError: (message: string) => void;
}

interface DeliveryAddressPickerState {
  address: string;
  phase: AddressPhase;
}

// A single centered status row shown in the list frame when there are no matches to
// list. Module-level (uses no instance state) to keep the class lint clean.
const renderMessage = (text: string, kind: MessageKind): JSX.Element => (
  <li className={styles.message} data-test={`delivery-location-address-${kind}`}>
    {text}
  </li>
);

export class DeliveryAddressPicker extends Component<
  DeliveryAddressPickerProps,
  DeliveryAddressPickerState
> {
  state: DeliveryAddressPickerState = { address: '', phase: { kind: 'idle' } };

  // One session per search: the keystroke predictions + the one Place Details lookup
  // bill as a single session. Cleared synchronously on pick (see handleSelect).
  private sessionToken?: google.maps.places.AutocompleteSessionToken;

  // Bumped on every keystroke and every pick. Both async callbacks capture the value
  // at issue time and bail if it changed, so a slow suggestions response or a slow
  // Place Details resolve can't clobber a newer intent.
  private requestGeneration = 0;

  private debouncedFetch = debounce(
    (input: string, generation: number) => this.fetchSuggestions(input, generation),
    SUGGEST_DEBOUNCE_MS,
  );

  componentWillUnmount(): void {
    this.debouncedFetch.cancel();
  }

  private handleChange = (value: string): void => {
    // Any edit invalidates in-flight responses and abandons a prior confirmation.
    this.requestGeneration += 1;
    this.props.onCleared();
    const trimmed = value.trim();
    if (!trimmed) {
      this.sessionToken = undefined;
      this.debouncedFetch.cancel();
      this.setState({ address: value, phase: { kind: 'idle' } });
      return;
    }
    this.setState({ address: value, phase: { kind: 'searching' } });
    this.debouncedFetch(trimmed, this.requestGeneration);
  };

  private fetchSuggestions = (input: string, generation: number): void => {
    if (!this.sessionToken) {
      this.sessionToken = createAutocompleteSessionToken();
    }
    getAddressSuggestions(
      input,
      {
        sessionToken: this.sessionToken,
        bias: this.props.bias,
        biasRadiusMeters: ADDRESS_BIAS_RADIUS_METERS,
        countryRestriction: ADDRESS_COUNTRY,
        types: ADDRESS_TYPES,
      },
      suggestions => {
        if (generation !== this.requestGeneration) {
          return; // superseded by a newer keystroke or a pick
        }
        this.setState({
          phase: suggestions.length
            ? { kind: 'matches', matches: suggestions }
            : { kind: 'noMatches' },
        });
      },
    );
  };

  private handleSelect = (suggestion: AddressSuggestion): void => {
    // Picking ends the search. Bump the generation so any in-flight suggestions are
    // dropped, and clear the session token SYNCHRONOUSLY: the getDetails call below
    // closes the session server-side, so a keystroke arriving while it resolves must
    // start a fresh token rather than reuse a spent one (which would fragment billing).
    this.requestGeneration += 1;
    const generation = this.requestGeneration;
    const token = this.sessionToken;
    this.sessionToken = undefined;
    resolveAddressLocation(suggestion.placeId, token, resolved => {
      // Drop a late resolve if the dispatcher kept typing while it was in flight.
      if (generation !== this.requestGeneration) {
        return;
      }
      if (!resolved) {
        this.props.onError(DELIVERY_LOCATION_LOOKUP_ERROR);
        return;
      }
      this.setState({
        address: resolved.formattedAddress || suggestion.description,
        phase: { kind: 'confirmed' },
      });
      this.props.onConfirmed(resolved);
    });
  };

  private renderList(): JSX.Element | null {
    const { phase } = this.state;
    if (phase.kind === 'confirmed') {
      return null; // the parent shows the map once an address is confirmed
    }
    let body: JSX.Element | JSX.Element[];
    switch (phase.kind) {
      case 'matches':
        body = phase.matches.map(match => (
          <li key={match.placeId}>
            <button
              type="button"
              className={styles.item}
              data-test="delivery-location-address-match"
              onClick={() => this.handleSelect(match)}
            >
              <span className={styles.itemPrimary}>{match.primaryText}</span>
              {match.secondaryText ? (
                <span className={styles.itemSecondary}>{match.secondaryText}</span>
              ) : null}
            </button>
          </li>
        ));
        break;
      case 'searching':
        body = renderMessage(DELIVERY_LOCATION_SEARCHING, 'searching');
        break;
      case 'noMatches':
        body = renderMessage(DELIVERY_LOCATION_NO_MATCHES, 'no-matches');
        break;
      case 'idle':
      default:
        body = renderMessage(DELIVERY_LOCATION_ADDRESS_PROMPT, 'empty');
        break;
    }
    return (
      <div
        className={cx(styles.previewSlot, styles.list)}
        data-test="delivery-location-address-matches"
      >
        <p className={styles.listLabel}>{DELIVERY_LOCATION_SELECT_MATCH}</p>
        <ul className={styles.listItems}>{body}</ul>
      </div>
    );
  }

  render(): JSX.Element {
    // display: contents on the root so .field and the list/map slot participate
    // directly in the modal body's flex column (keeps one flex-fill child).
    return (
      <div className={styles.root} data-test="delivery-location-address-mode">
        <div className={styles.field}>
          <TextField
            fullWidth
            label={DELIVERY_LOCATION_ENTER_ADDRESS}
            value={this.state.address}
            inputProps={{ 'data-test': 'delivery-location-address-input' }}
            onChange={e => this.handleChange(e.target.value)}
          />
        </div>
        {this.renderList()}
      </div>
    );
  }
}

export default DeliveryAddressPicker;
